import {LinkProps} from 'platform/foundation';

import i18n from '@dms/i18n';
import {rentalOrderRoutes, settingsRoutes, warehouseRoutes, workshopRoutes} from '@dms/routes';
import {ORDER_TABS, queryParams} from '@dms/shared';

import {composePath} from 'shared/web';

export interface IssueRestriction {
  code: string;
  message?: string;
}

export type ContextTarget = 'service-order' | 'direct-sale' | undefined;

export interface RestrictionActionContext {
  rentalOrderId?: string;
}

export interface RestrictionAlertConfig {
  messageKey: string;
  fallbackMessage: string;
  context: 'general' | 'service-order' | 'direct-sale';
  action?: (
    contextId?: string,
    contextTarget?: ContextTarget,
    actionContext?: RestrictionActionContext
  ) => LinkProps | null;
}

const RESTRICTION_CODE_MAP: Record<string, RestrictionAlertConfig> = {
  'ACL-FE-ER-0001': {
    messageKey: 'entity.checkout.restrictions.aclFeEr0001',
    fallbackMessage: 'The user does not have the permission',
    context: 'general',
  },
  'CHK-FE-ER-0009': {
    messageKey: 'entity.checkout.restrictions.chkFeEr0009',
    fallbackMessage: 'No checkout',
    context: 'general',
  },
  'CHK-FE-ER-0010': {
    messageKey: 'entity.checkout.restrictions.chkFeEr0010',
    fallbackMessage: 'No checkout payment',
    context: 'general',
  },
  'CHK-FE-ER-0011': {
    messageKey: 'entity.checkout.restrictions.chkFeEr0011',
    fallbackMessage: 'There is already an invoice for payment',
    context: 'general',
  },
  'CHK-FE-ER-0012': {
    messageKey: 'entity.checkout.restrictions.chkFeEr0012',
    fallbackMessage: 'Not all deposits have been paid',
    context: 'general',
  },
  'CHK-FE-ER-0013': {
    messageKey: 'entity.checkout.restrictions.chkFeEr0013',
    fallbackMessage: 'A document for payment has already been issued',
    context: 'general',
  },
  'CHK-FE-ER-0027': {
    messageKey: 'entity.checkout.restrictions.chkFeEr0027',
    fallbackMessage: 'Exchange rate must be greater than 0',
    context: 'general',
  },
  'CHK-FE-ER-0030': {
    messageKey: 'entity.checkout.restrictions.chkFeEr0030',
    fallbackMessage: 'Payment amount is higher than bank transfer limit for this customer',
    context: 'general',
  },
  'CHK-FE-ER-0031': {
    messageKey: 'entity.checkout.restrictions.chkFeEr0031',
    fallbackMessage: 'Customer details does not contain an address',
    context: 'general',
  },
  'CHK-FE-ER-0032': {
    messageKey: 'entity.checkout.restrictions.chkFeEr0032',
    fallbackMessage: "Payment method can't be empty",
    context: 'general',
  },
  'CHK-FE-ER-0034': {
    messageKey: 'entity.checkout.restrictions.chkFeEr0034',
    fallbackMessage: 'Payment amount must be greater than zero',
    context: 'general',
  },
  'CHK-FE-ER-0040': {
    messageKey: 'entity.checkout.restrictions.chkFeEr0040',
    fallbackMessage: 'The selected payment method is not allowed',
    context: 'general',
  },
  'CHK-FE-ER-0042': {
    messageKey: 'entity.checkout.restrictions.chkFeEr0042',
    fallbackMessage:
      'A service order is linked to the rental order and its status does not allow you to close the service order',
    context: 'service-order',
    action: buildRentalOrderOrServiceOrderDetailAction,
  },
  'CHK-FE-ER-0043': {
    messageKey: 'entity.checkout.restrictions.chkFeEr0043',
    fallbackMessage: "IBAN and SWIFT of tenant's bank account must be provided",
    context: 'general',
    action: () => {
      const title = i18n.t('general.actions.view');
      return {
        href: settingsRoutes.billingInformation,
        title,
      };
    },
  },
  'SVC-FE-ER-0017': {
    messageKey: 'entity.checkout.restrictions.svcFeEr0017',
    fallbackMessage: 'No service case',
    context: 'service-order',
    action: buildServiceOrderDetailAction,
  },
  'SVC-FE-ER-0021': {
    messageKey: 'entity.checkout.restrictions.svcFeEr0021',
    fallbackMessage: 'No service order',
    context: 'service-order',
    action: buildServiceOrderDetailAction,
  },
  'SVC-FE-ER-0129': {
    messageKey: 'entity.checkout.restrictions.svcFeEr0129',
    fallbackMessage:
      'Cannot close a service order. The order contains spare parts that have not yet been issued',
    context: 'service-order',
    action: buildServiceOrderDetailAction,
  },
  'SVC-FE-ER-0130': {
    messageKey: 'entity.checkout.restrictions.svcFeEr0130',
    fallbackMessage:
      'Cannot close a service order. The order contains work that is not assigned to the mechanic',
    context: 'service-order',
    action: buildServiceOrderDetailAction,
  },
  'SVC-FE-ER-0131': {
    messageKey: 'entity.checkout.restrictions.svcFeEr0131',
    fallbackMessage: "Cannot close a service order. The order doesn't contain any work items",
    context: 'service-order',
    action: buildServiceOrderDetailAction,
  },
  'SVC-FE-ER-0133': {
    messageKey: 'entity.checkout.restrictions.svcFeEr0133',
    fallbackMessage:
      "Cannot close a service order. Final selling price for invoice can't be negative",
    context: 'service-order',
    action: buildServiceOrderDetailAction,
  },
  'SVC-FE-ER-0194': {
    messageKey: 'entity.checkout.restrictions.svcFeEr0194',
    fallbackMessage:
      'The service order cannot be closed. Not all mandatory fields for vehicle are filled in',
    context: 'service-order',
    action: buildServiceOrderDetailAction,
  },
  'WRH-FE-ER-0098': {
    messageKey: 'entity.checkout.restrictions.wrhFeEr0098',
    fallbackMessage: "Cannot close a direct sale. Document doesn't contain any items",
    context: 'direct-sale',
    action: buildDirectSaleDetailAction,
  },
  'WRH-FE-ER-0099': {
    messageKey: 'entity.checkout.restrictions.wrhFeEr0099',
    fallbackMessage:
      "Cannot close a direct sale. Final selling price for invoice can't be negative",
    context: 'direct-sale',
    action: buildDirectSaleDetailAction,
  },
  'WRH-FE-ER-0100': {
    messageKey: 'entity.checkout.restrictions.wrhFeEr0100',
    fallbackMessage:
      'Cannot close a direct sale. Document contains spare parts that have not yet been issued',
    context: 'direct-sale',
    action: buildDirectSaleDetailAction,
  },
};

function buildServiceOrderDetailAction(
  contextId?: string,
  contextTarget?: ContextTarget
): LinkProps | null {
  if (contextTarget !== 'service-order' || !contextId) {
    return null;
  }

  const path = composePath(workshopRoutes.serviceCaseDetail, {
    params: {id: contextId},
    queryParams: {
      [queryParams.SERVICE_CASE_ORDER_TAB]: ORDER_TABS.JOBS,
    },
  });

  const title = i18n.t('general.actions.view');
  return {
    href: path,
    title,
  };
}

function buildRentalOrderOrServiceOrderDetailAction(
  contextId?: string,
  contextTarget?: ContextTarget,
  actionContext?: RestrictionActionContext
): LinkProps | null {
  if (contextTarget !== 'service-order') {
    return null;
  }

  if (actionContext?.rentalOrderId) {
    const path = composePath(rentalOrderRoutes.vehicleAndCustomer, {
      params: {id: actionContext.rentalOrderId},
      isSearchParamsPreserved: false,
    });

    const title = i18n.t('general.actions.view');
    return {
      href: path,
      title,
    };
  }

  return buildServiceOrderDetailAction(contextId, contextTarget);
}

function buildDirectSaleDetailAction(
  contextId?: string,
  contextTarget?: ContextTarget
): LinkProps | null {
  if (contextTarget !== 'direct-sale' || !contextId) {
    return null;
  }

  const path = composePath(warehouseRoutes.directSalesDetailOverview, {
    params: {id: contextId},
    queryParams: {
      [queryParams.COMPONENT_SECTIONS_TAB]: 'directSale-requestTab',
    },
  });

  const title = i18n.t('general.actions.view');
  return {
    href: path,
    title,
  };
}

export function getRestrictionAlertConfig(code: string): RestrictionAlertConfig | null {
  return RESTRICTION_CODE_MAP[code] ?? null;
}

export function getMessage(code: string): string {
  const config = getRestrictionAlertConfig(code);
  if (!config) {
    return i18n.t('entity.checkout.labels.generalError');
  }

  return i18n.t(config.messageKey, {defaultValue: config.fallbackMessage});
}

export function getAction(
  code: string,
  contextId?: string,
  contextTarget?: ContextTarget,
  actionContext?: RestrictionActionContext
): LinkProps | null {
  const config = getRestrictionAlertConfig(code);
  if (!config || !config.action) {
    return null;
  }

  return config.action(contextId, contextTarget, actionContext);
}

export function shouldShowAlert(code: string, contextTarget?: ContextTarget): boolean {
  const config = getRestrictionAlertConfig(code);
  if (!config) {
    return true;
  }

  if (config.context === 'general') {
    return true;
  }
  if (config.context === 'service-order') {
    return contextTarget === 'service-order';
  }
  if (config.context === 'direct-sale') {
    return contextTarget === 'direct-sale';
  }

  return false;
}
